import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, Check } from 'lucide-react';

const LanguageSelector = ({ isMobile = false }) => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const languages = [
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'tr', name: 'Türkçe', flag: '🇹🇷' }
  ];

  const currentLang = languages.find(l => l.code === i18n.language) || languages[0];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const changeLanguage = (langCode) => {
    i18n.changeLanguage(langCode);
    setIsOpen(false);
  };

  if (isMobile) {
    return (
      <div className="flex gap-2 px-4 py-2">
        {languages.map((lang) => (
          <button
            key={lang.code}
            onClick={() => changeLanguage(lang.code)}
            className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
              i18n.language === lang.code
                ? 'bg-[#00d4aa]/10 text-[#00d4aa] border border-[#00d4aa]'
                : 'bg-[#16161f] text-[#9ca3af] border border-[#2a2a3a] hover:border-[#00d4aa]'
            }`}
            data-testid={`mobile-lang-${lang.code}`}
          >
            <span>{lang.flag}</span>
            <span>{lang.code.toUpperCase()}</span>
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#16161f] border border-[#2a2a3a] hover:border-[#00d4aa] transition-colors"
        data-testid="language-selector"
      >
        <Globe className="w-4 h-4 text-[#9ca3af]" />
        <span className="text-sm text-[#f0f0f5]">{currentLang.flag}</span>
        <span className="text-sm text-[#9ca3af]">{currentLang.code.toUpperCase()}</span>
      </button>

      {isOpen && (
        <div className="absolute top-full right-0 mt-2 w-40 bg-[#16161f] border border-[#2a2a3a] rounded-lg shadow-xl overflow-hidden z-50">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => changeLanguage(lang.code)}
              className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors ${
                i18n.language === lang.code
                  ? 'bg-[#00d4aa]/10 text-[#00d4aa]'
                  : 'text-[#f0f0f5] hover:bg-[#2a2a3a]'
              }`}
              data-testid={`lang-option-${lang.code}`}
            >
              <span className="text-lg">{lang.flag}</span>
              <span className="flex-1 text-left">{lang.name}</span>
              {i18n.language === lang.code && (
                <Check className="w-4 h-4 text-[#00d4aa]" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
